import { Component, Inject, Vue } from 'vue-property-decorator';
import AlertService from '@/shared/alert/alert.service';
import CatalogoService from '@/shared/services/catalogo.service';
import { helpers, maxLength, minLength, required, requiredIf } from 'vuelidate/lib/validators';
import EntityFactory from '@/shared/entity-commons/entity.factory';
import CatalogosFactory from '@/shared/entity-commons/catalogos.factory';
import UtilityMixin from '@/shared/mixins/utility.mixin';
import { mixins } from 'vue-class-component';
import { ActualizaRegistro, NuevoRegistro, RegistroDomicilio, ReniecytApi } from '@/shared/openapi-reniecyt-client/api';
import { Domicilio, Contacto, CvuApi, AreaConocimiento } from '@conacyt/perfil-usuario-client';
import { mapGetters } from 'vuex';
import * as Constants from '@/constants';

const VALIDATIONS = function () {
  return {
    nuevaSolicitud: {
      tipoSolicitante: {
        required,
      },
      sector: {
        required: requiredIf(() => this.esMoral),
      },
      rfc: {
        required: requiredIf(() => !this.sectorSocial),
        minLength: minLength(1),
        maxLength: maxLength(13),
        regex: regexValue => {
          if (this.esMoral && this.nuevaSolicitud.sector?.id === Constants.CLAVE_SECTOR_SOCIAL) {
            return true; // Permite valores nulos
          }
          return (
            regexValue &&
            (this.esMoral ? Constants.RFC_PERSONA_MORAL_REGEX.test(regexValue) : Constants.RFC_PERSONA_FISICA_REGEX.test(regexValue))
          );
        },
      },
      razonSocial: {
        required: requiredIf(() => this.esMoral),
        minLength: minLength(1),
        maxLength: maxLength(200),
      },
      tipoInstitucionEmpresa: {
        required: requiredIf(() => this.esMoral && this.sectorPrivada),
      },
      nivel: {
        required: requiredIf(() => this.esMoral && this.sectorPublica),
      },
      tipoInstitucionEntidad: {
        required: requiredIf(() => this.esMoral && this.sectorPublica && this.tipoInstitucionEntidadB),
      },
      grupoSocial: {
        required: requiredIf(() => this.esMoral && this.sectorSocial),
      },
      especificar: {
        required: requiredIf(() => this.esMoral && this.sectorSocial),
        minLength: minLength(1),
        maxLength: maxLength(2000),
      },
      clasificacion: {
        required: requiredIf(() => this.esMoral && this.clasificacionB),
      },
      figuraJuridica: {
        required: requiredIf(
          () => (this.esMoral && this.tipoInsEmpresaEmpresa) || (this.esMoral && this.sectorPublica && !this.edicionAreaConocmiento)
        ),
      },
      scian: {
        required: requiredIf(() => this.esMoral && this.tipoInsEmpresaEmpresa),
      },
      ocde: {
        required: requiredIf(() => this.esMoral && this.tipoInsEmpresaEmpresa),
      },
      areaConocimiento: {
        required: requiredIf(() => this.esMoral && this.tipoInsEmpresaEmpresa),
      },
      tamanioEmpresa: {
        required: requiredIf(() => this.esMoral && this.tipoInsEmpresaEmpresa),
      },
      //Campos Fisica
      nombre: {
        required: requiredIf(() => !this.esMoral),
        minLength: minLength(1),
        maxLength: maxLength(256),
      },
      primerApellido: {
        required: requiredIf(() => !this.esMoral),
        minLength: minLength(1),
        maxLength: maxLength(256),
      },
      segundoApellido: {
        minLength: minLength(1),
        maxLength: maxLength(256),
      },
      actividadPreponderante: {
        required: requiredIf(() => !this.esMoral),
      },
    },
  };
};

@Component({
  validations: VALIDATIONS,
  computed: {
    ...mapGetters(['account']),
  },
})
export default class NuevaSolicitudComponent extends mixins(UtilityMixin) {
  @Inject('alertService') private alertService: () => AlertService;
  @Inject('catalogoService') private catalogoService: () => CatalogoService;
  @Inject('reniecytApi') private reniecytApi: () => ReniecytApi;
  @Inject('perfilUsuarioApi') private perfilUsuarioApi: () => CvuApi;

  public account;

  public nuevaSolicitud = EntityFactory.getNuevaSolicitud();
  public nuevoRegistro: NuevoRegistro = EntityFactory.getNuevoRegistro();
  public actualizaRegistro: ActualizaRegistro = EntityFactory.getActualizaRegistro();
  public miPerfil: any;
  public domicilio: Domicilio = EntityFactory.getDomicilio();
  public domicilioRenicyt: RegistroDomicilio = EntityFactory.getDomicilioReniecyt();
  public mediosContacto: Array<Contacto> = [];
  public mediosContactoReniecyt: Array<any> = [];

  public esMoral = true;
  public tipoSolicitanteOptions: Array<any> = [];
  public sectorOptions: Array<any> = [];
  public tipoInstitucionEmpresaOptions: Array<any> = [];
  public figuraJuridicaOptions: Array<any> = [];
  public tamanioEmpresaOptions: Array<any> = [];
  public nivelOptions: Array<any> = [];
  public tipoInstitucionEntidadOptions: Array<any> = [];
  public clasificacionOptions: Array<any> = [];
  public grupoSocialOptions: Array<any> = [];
  public actividadPreponderanteOptions: Array<any> = [];

  public sectorPrivada = false;
  public sectorPublica = false;
  public sectorSocial = false;

  public tipoInsEmpresaEmpresa = false;
  public tipoInsEmpresaEducacion = false;

  //Valida que tengan valor desde el padre
  public validChildScian = false;
  public validChildOcde = false;
  public validChildArea = false;

  public tipoInstitucionEntidadB = false;
  public clasificacionB = false;
  public readonlyRfc = false;
  private idRegistro = null;
  public edicionAreaConocmiento = false;
  public reseteoSede = false;
  public noRegistro = null;

  public juridicaReadonly = false;

  mounted() {
    this.idRegistro = EntityFactory.obtenerIdRegistro();
    this.reseteoSede = EntityFactory.obtenerReseteoSede();
    if (this.idRegistro && EntityFactory.obtenerEdicionSolicitudArea()) {
      this.edicionAreaConocmiento = true;
    }
    this.initCatalogos();
    if (!this.edicionAreaConocmiento) {
      this.recuperaPerfil();

      if (!this.reseteoSede) this.existeRegistroFisica(); //No es necesario buscar cuando esta activado el reseteo
    } else {
      //Se activan variables ya que de momento solo se pone en edicion que son morales y tipo empresa, ya que son los unicos que tienen area de conocimiento
      this.esMoral = true;
      this.tipoInsEmpresaEmpresa = true;
    }

    if (this.reseteoSede && this.idRegistro) {
      this.esMoral = true;
      this.tipoSolicitanteOptions = this.tipoSolicitanteOptions.filter((option: any) => option.value.id !== '2');
      this.consultaDatosGeneralesRegistro();
    }
  }

  private initCatalogos(): void {
    //TODO Se quedan opciones de catalogos en EntityFactory de manera temporal en lo que saci los crea
    this.tipoSolicitanteOptions = CatalogosFactory.getTipoSolicitanteOptions();
    this.sectorOptions = CatalogosFactory.getSectorOptions();
    this.tipoInstitucionEmpresaOptions = CatalogosFactory.getTipoInstitucionEmpresaOptions();
    this.figuraJuridicaOptions = CatalogosFactory.getFiguraJuridicaOptions();
    this.tamanioEmpresaOptions = CatalogosFactory.getTamanioEmpresaOptions();
    this.nivelOptions = CatalogosFactory.getNivelOptions();
    // Cuando sea de saci, meter en una variable tipoInstitucionEntidadOptions para manipular los datos y llamar constantente al servicio;
    this.tipoInstitucionEntidadOptions = CatalogosFactory.getTipoInstitucionEntidadOptions();
    const idsDescartar = [7, 8, 9];
    this.clasificacionOptions = this.descartaCatalogoIds(CatalogosFactory.getClasificacionOptions(), idsDescartar);
    this.grupoSocialOptions = CatalogosFactory.getGrupoSocialOptions();
    this.actividadPreponderanteOptions = CatalogosFactory.getActividadPrepnderanteOptions();
  }

  private recuperaPerfil(): void {
    this.perfilUsuarioApi()
      .getPerfil(this.account.cvu, this.account.login)
      .then(response => {
        this.miPerfil = response.data?.principal;
        if (this.miPerfil.domicilio == undefined) {
          this.alertService().showWarning(this, this.$t('nueva-solicitud.alert.domicilio').toString());
        }
        if (this.miPerfil.mediosContacto == undefined) {
          this.alertService().showWarning(this, this.$t('nueva-solicitud.alert.mediosContacto').toString());
        }
        if (this.miPerfil.rfc == undefined) {
          this.alertService().showWarning(this, this.$t('nueva-solicitud.alert.rfc').toString());
        }
        if (this.miPerfil.estadoCivil == undefined) {
          this.alertService().showWarning(this, this.$t('nueva-solicitud.alert.estadoCivil').toString());
        }
      })
      .catch(error => {
        if (error.response?.data?.status !== 404) {
          throw error;
        }
      });
  }

  private asignaNombreCompleto() {
    this.nuevaSolicitud.nombre = this.miPerfil?.nombre || null;
    this.nuevaSolicitud.primerApellido = this.miPerfil?.primerApellido || null;
    this.nuevaSolicitud.segundoApellido = this.miPerfil?.segundoApellido || null;
  }

  public cambiaRadio(e: string) {
    if (e == Constants.CLAVE_MORAL) {
      this.esMoral = true;
      this.nuevaSolicitud = EntityFactory.getNuevaSolicitud();
      this.readonlyRfc = false;
    }
    if (e == Constants.CLAVE_FISICA) {
      this.tipoInstitucionEmpresaOptions = CatalogosFactory.getTipoInstitucionEmpresaOptions();
      this.tipoInstitucionEmpresaOptions = this.filtraCatalogoIdObject(CatalogosFactory.getTipoInstitucionEmpresaOptions(), 8);
      this.esMoral = false;
      this.setValoresDefaultSectorTotal();
      const radio = this.nuevaSolicitud.tipoSolicitante;
      this.nuevaSolicitud = EntityFactory.getNuevaSolicitud();
      this.nuevaSolicitud.tipoSolicitante = radio;
      this.nuevaSolicitud.tipoInstitucionEmpresa = this.tipoInstitucionEmpresaOptions[0].value;
      this.asignaNombreCompleto();
      this.readonlyRfc = true;
      this.nuevaSolicitud.rfc = this.miPerfil?.rfc;
    }
    this.$v.nuevaSolicitud.$reset();
  }

  public cambiaSector(e: string) {
    this.juridicaReadonly = false;
    this.juridicaReadonly = null;

    switch (e) {
      case Constants.CLAVE_SECTOR_PRIVADA:
        this.sectorPrivada = true;
        this.sectorPublica = false;
        this.sectorSocial = false;
        this.setValoresDefaultSectorPublico();
        this.tipoInstitucionEmpresaOptions = this.descartaCatalogoId(CatalogosFactory.getTipoInstitucionEmpresaOptions(), 8);
        break;

      case Constants.CLAVE_SECTOR_PUBLICA:
        this.setValoresDefaultSector();
        this.sectorPublica = true;
        this.setValoresDefaultSectorEmpresa();
        this.nuevaSolicitud.tipoInstitucionEmpresa = null;
        this.nuevaSolicitud.figuraJuridica = this.filtraCatalogoId(CatalogosFactory.getFiguraJuridicaOptions(), 2);
        this.juridicaReadonly = true;
        break;

      case Constants.CLAVE_SECTOR_SOCIAL:
        this.setValoresDefaultSector();
        this.sectorSocial = true;
        this.setValoresDefaultSectorEmpresa();
        this.setValoresDefaultSectorPublico();
        this.nuevaSolicitud.tipoInstitucionEmpresa = null;
        break;

      default:
        this.setValoresDefaultSectorTotal();
        break;
    }
    this.$v.nuevaSolicitud.$reset();
  }

  public cambiaTipoInstitucionEmpresa(e: string) {
    switch (e) {
      case Constants.CLAVE_TIPO_INST_EMPRESA:
        this.tipoInsEmpresaEmpresa = true;
        this.tipoInsEmpresaEducacion = false;
        break;
      case Constants.CLAVE_TIPO_INST_EDUCACION:
        this.setValoresDefaultSectorEmpresa();
        this.tipoInsEmpresaEducacion = true;
        break;
      default:
        this.setValoresDefaultSectorEmpresa();
        break;
    }
  }

  private setValoresDefaultSectorEmpresa() {
    this.tipoInsEmpresaEmpresa = false;
    this.tipoInsEmpresaEducacion = false;
    this.validChildScian = false;
    this.validChildOcde = false;
    this.validChildArea = false;
    this.nuevaSolicitud.figuraJuridica = null;
    this.nuevaSolicitud.clasificacion = null;
    this.nuevaSolicitud.scian = EntityFactory.getScian();
    this.nuevaSolicitud.ocde = EntityFactory.getOcde();
    this.nuevaSolicitud.areaConocimiento = EntityFactory.getAreaConocimiento();
    this.nuevaSolicitud.tamanioEmpresa = null;
  }

  private setValoresDefaultSectorPublico() {
    this.tipoInstitucionEntidadB = false;
    this.nuevaSolicitud.nivel = null;
    this.nuevaSolicitud.tipoInstitucionEntidad = null;
    this.nuevaSolicitud.clasificacion = null;
    this.nuevaSolicitud.figuraJuridica = null;
  }

  private setValoresDefaultSectorSocial() {
    this.nuevaSolicitud.tipoInstitucionEmpresa = null;
    this.nuevaSolicitud.especificar = null;
  }

  private setValoresDefaultSector() {
    this.sectorPrivada = false;
    this.sectorPublica = false;
    this.sectorSocial = false;
  }

  private setValoresDefaultSectorTotal() {
    this.setValoresDefaultSector();
    this.setValoresDefaultSectorEmpresa();
    this.setValoresDefaultSectorPublico();
    this.setValoresDefaultSectorSocial();
    this.nuevaSolicitud.tipoInstitucionEmpresa = null;
  }

  public cambiaNivel(e: string) {
    this.tipoInstitucionEntidadB = e != Constants.CLAVE_NIVEL_MUNICIPAL;
    this.nuevaSolicitud.tipoInstitucionEntidad = null;
    this.clasificacionB = false;
    this.nuevaSolicitud.clasificacion = null;
    if (this.tipoInstitucionEntidadB) {
      if (e == Constants.CLAVE_NIVEL_FEDERAL) {
        //Todos los datos
        this.tipoInstitucionEntidadOptions = CatalogosFactory.getTipoInstitucionEntidadOptions();
        if (this.nuevaSolicitud.tipoInstitucionEntidad) {
          this.cambiaTipoInstEntidad(this.nuevaSolicitud.tipoInstitucionEntidad.id);
        }
      }

      if (e == Constants.CLAVE_NIVEL_ESTATAL) {
        this.tipoInstitucionEntidadOptions = CatalogosFactory.getTipoInstitucionEntidadOptions().filter(function (tipoInst) {
          return tipoInst.value.id != Constants.CLAVE_TIPO_INST_ENTIDAD_CONACYT;
        });
      }
    } else {
      this.nuevaSolicitud.tipoInstitucionEntidad = null;
      this.clasificacionB = false;
      this.nuevaSolicitud.clasificacion = null;
    }
  }

  public cambiaTipoInstEntidad(e: string) {
    const esEducacion = e === Constants.CLAVE_TIPO_INST_ENTIDAD_EDUCACION;
    const esSectorPublico = e === Constants.CLAVE_TIPO_INST_ENTIDAD_SEC_PUBLICO;
    const nivelId = this.nuevaSolicitud.nivel?.id;

    this.clasificacionB = esEducacion || esSectorPublico;
    this.nuevaSolicitud.clasificacion = null;

    if (this.clasificacionB) {
      const catalogoClasificacion = CatalogosFactory.getClasificacionOptions();

      if (esEducacion || (esSectorPublico && nivelId === Constants.CLAVE_NIVEL_ESTATAL)) {
        const idsDescartar = [7, 8, 9];
        this.clasificacionOptions = this.descartaCatalogoIds(catalogoClasificacion, idsDescartar);
      } else if (esSectorPublico) {
        const idsDescartar = [4, 5, 6];
        this.clasificacionOptions = this.descartaCatalogoIds(catalogoClasificacion, idsDescartar);
      }
    }
  }

  public save(nSolictud) {
    if (!this.$v.nuevaSolicitud.$invalid) {
      if (this.sectorPrivada && this.nuevaSolicitud.tipoInstitucionEmpresa.id == Constants.CLAVE_TIPO_INST_EMPRESA) {
        if (
          !this.validaAreaConocimiento(this.nuevaSolicitud) ||
          !this.validaOcde(this.nuevaSolicitud) ||
          !this.validaScian(this.nuevaSolicitud)
        ) {
          this.validChildArea = true;
          this.validChildOcde = true;
          this.validChildScian = true;
          return;
        }
      }
      this.setSolicitudInNuevoRegistro(nSolictud);
      this.reniecytApi()
        .createNuevoRegistro(this.nuevoRegistro)
        .then(res => {
          this.alertService().showSuccess(this, this.$t('nueva-solicitud.alert.save').toString());
          this.nuevaSolicitud = EntityFactory.getNuevaSolicitud();
          this.setValoresDefaultSectorTotal();
          this.clasificacionB = false;
          this.tipoInsEmpresaEducacion = false;
          this.nuevaSolicitud.sector = null;
          this.$v.nuevaSolicitud.$reset();
          this.$router.push({ name: 'ConsultaSolicitudes' });
        })
        .catch(err => {
          if (err?.response?.data?.detail) {
            this.alertService().showError(this, err.response.data.detail);
          } else {
            this.alertService().showError(this, this.$t('error.internalServerError').toString());
          }
        });
    } else {
      if (this.nuevaSolicitud.rfc === null && this.nuevaSolicitud.sector?.id != Constants.CLAVE_SECTOR_SOCIAL) {
        this.nuevaSolicitud.rfc = '';
      }
      if (!this.esMoral) {
        this.nuevaSolicitud.rfc = this.miPerfil?.rfc != null && this.miPerfil?.rfc != '' ? this.miPerfil?.rfc : '';
      }
      this.$v.nuevaSolicitud.$touch();
    }
  }

  public cancel(): void {
    window.location.reload();
  }

  private setSolicitudInNuevoRegistro(nuevaSolicitud: any): void {
    // Asignar valores a registro
    this.nuevoRegistro.registro.tipoSolicitudId = nuevaSolicitud.tipoSolicitante?.id || null;
    this.nuevoRegistro.registro.tipoSolicitudNombre = nuevaSolicitud.tipoSolicitante?.nombre || null;
    this.nuevoRegistro.registro.rfc = nuevaSolicitud.rfc || null;
    this.nuevoRegistro.registro.nombre = nuevaSolicitud.nombre || null;
    this.nuevoRegistro.registro.primerApellido = nuevaSolicitud.primerApellido || null;
    this.nuevoRegistro.registro.segundoApellido = nuevaSolicitud.segundoApellido || null;
    this.nuevoRegistro.registro.actividadId = nuevaSolicitud.actividadPreponderante?.id || null;
    this.nuevoRegistro.registro.actividadNombre = nuevaSolicitud.actividadPreponderante?.nombre || null;
    this.nuevoRegistro.registro.sectorId = nuevaSolicitud.sector?.id || null;
    this.nuevoRegistro.registro.sectorNombre = nuevaSolicitud.sector?.nombre || null;
    this.nuevoRegistro.registro.razonSocial = nuevaSolicitud.razonSocial || null;
    this.nuevoRegistro.registro.tipoInstitucionId =
      nuevaSolicitud.tipoInstitucionEmpresa?.id || nuevaSolicitud.tipoInstitucionEntidad?.id || null;
    this.nuevoRegistro.registro.tipoInstitucionNombre =
      nuevaSolicitud.tipoInstitucionEmpresa?.nombre || nuevaSolicitud.tipoInstitucionEntidad?.nombre || null;
    this.nuevoRegistro.registro.nivelId = nuevaSolicitud.nivel?.id || null;
    this.nuevoRegistro.registro.nivelNombre = nuevaSolicitud.nivel?.nombre || null;
    this.nuevoRegistro.registro.figuraJuridicaId = nuevaSolicitud.figuraJuridica?.id || null;
    this.nuevoRegistro.registro.figuraJuridicaNombre = nuevaSolicitud.figuraJuridica?.nombre || null;
    this.nuevoRegistro.registro.grupoSocialId = nuevaSolicitud.grupoSocial?.id || null;
    this.nuevoRegistro.registro.grupoSocialNombre = nuevaSolicitud.grupoSocial?.nombre || null;
    this.nuevoRegistro.registro.grupoSocialEspecificar = nuevaSolicitud.especificar || null;
    this.nuevoRegistro.registro.clasificacionId = nuevaSolicitud.clasificacion?.id || null;
    this.nuevoRegistro.registro.clasificacionNombre = nuevaSolicitud.clasificacion?.nombre || null;
    this.nuevoRegistro.registro.tamanioId = nuevaSolicitud.tamanioEmpresa?.id || null;
    this.nuevoRegistro.registro.tamanioNombre = nuevaSolicitud.tamanioEmpresa?.nombre || null;

    // Asignar valores a persona
    this.nuevoRegistro.persona.tipoId = nuevaSolicitud.tipoSolicitante?.id || null;
    this.nuevoRegistro.persona.tipoNombre = nuevaSolicitud.tipoSolicitante?.nombre || null;
    this.nuevoRegistro.persona.rfc = this.miPerfil?.rfc || null;
    this.nuevoRegistro.persona.curp = this.miPerfil?.curp || null;
    this.nuevoRegistro.persona.cvu = this.account.cvu || null;
    this.nuevoRegistro.persona.nombre = this.miPerfil.nombre || null;
    this.nuevoRegistro.persona.primerApellido = this.miPerfil.primerApellido || null;
    this.nuevoRegistro.persona.segundoApellido = this.miPerfil.segundoApellido || null;
    this.nuevoRegistro.persona.generoId = this.miPerfil.sexo?.id || null;
    this.nuevoRegistro.persona.generoNombre = this.miPerfil.sexo?.nombre || null;
    this.nuevoRegistro.persona.estadoConyugalId = this.miPerfil.estadoCivil?.id || null;
    this.nuevoRegistro.persona.estadoConyugalNombre = this.miPerfil.estadoCivil?.nombre || null;
    this.nuevoRegistro.persona.nacionalidadId = this.miPerfil.nacionalidad?.id || null;
    this.nuevoRegistro.persona.nacionalidadNombre = this.miPerfil.nacionalidad?.nombre || null;
    this.nuevoRegistro.persona.paisNacimientoId = this.miPerfil.paisNacimiento?.id || null;
    this.nuevoRegistro.persona.paisNacimientoNombre = this.miPerfil.paisNacimiento?.nombre || null;
    this.nuevoRegistro.persona.entidadFederativaId = this.miPerfil.entidadFederativa?.clave || null;
    this.nuevoRegistro.persona.entidadFederativaNombre = this.miPerfil.entidadFederativa?.nombre || null;
    this.nuevoRegistro.persona.fechaRegistro = Constants.FECHA_ACTUAL.toISOString();

    // Asignar valores a sector
    this.nuevoRegistro.sector.scianSectorId = nuevaSolicitud.scian?.sector?.id || null;
    this.nuevoRegistro.sector.scianSectorNombre = nuevaSolicitud.scian?.sector?.nombre || null;
    this.nuevoRegistro.sector.scianSubsectorId = nuevaSolicitud.scian?.subsector?.id || null;
    this.nuevoRegistro.sector.scianSubsectorNombre = nuevaSolicitud.scian?.subsector?.nombre || null;
    this.nuevoRegistro.sector.scianRamaId = nuevaSolicitud.scian?.rama?.id || null;
    this.nuevoRegistro.sector.scianRamaNombre = nuevaSolicitud.scian?.rama?.nombre || null;
    this.nuevoRegistro.sector.scianSubramaId = nuevaSolicitud.scian?.subrama?.id || null;
    this.nuevoRegistro.sector.scianSubramaNombre = nuevaSolicitud.scian?.subrama?.nombre || null;
    this.nuevoRegistro.sector.scianClaseId = nuevaSolicitud.scian?.clase?.id || null;
    this.nuevoRegistro.sector.scianClaseNombre = nuevaSolicitud.scian?.clase?.nombre || null;
    this.nuevoRegistro.sector.ocdeSectorId = nuevaSolicitud.ocde?.sector?.id || null;
    this.nuevoRegistro.sector.ocdeSectorNombre = nuevaSolicitud.ocde?.sector?.nombre || null;
    this.nuevoRegistro.sector.ocdeDivisionId = nuevaSolicitud.ocde?.division?.id || null;
    this.nuevoRegistro.sector.ocdeDivisionNombre = nuevaSolicitud.ocde?.division?.nombre || null;
    this.nuevoRegistro.sector.ocdeGrupoId = nuevaSolicitud.ocde?.grupo?.id || null;
    this.nuevoRegistro.sector.ocdeGrupoNombre = nuevaSolicitud.ocde?.grupo?.nombre || null;
    this.nuevoRegistro.sector.ocdeClaseId = nuevaSolicitud.ocde?.clase?.id || null;
    this.nuevoRegistro.sector.ocdeClaseNombre = nuevaSolicitud.ocde?.clase?.nombre || null;
    this.nuevoRegistro.sector.areaAreaId = nuevaSolicitud.areaConocimiento?.area?.id || null;
    this.nuevoRegistro.sector.areaAreaNombre = nuevaSolicitud.areaConocimiento?.area?.nombre || null;
    this.nuevoRegistro.sector.areaCampoId = nuevaSolicitud.areaConocimiento?.campo?.id || null;
    this.nuevoRegistro.sector.areaCampoNombre = nuevaSolicitud.areaConocimiento?.campo?.nombre || null;
    this.nuevoRegistro.sector.areaDisciplinaId = nuevaSolicitud.areaConocimiento?.disciplina?.id || null;
    this.nuevoRegistro.sector.areaDisciplinaNombre = nuevaSolicitud.areaConocimiento?.disciplina?.nombre || null;
    this.nuevoRegistro.sector.areaSubdisciplinaId = nuevaSolicitud.areaConocimiento?.subdisciplina?.id || null;
    this.nuevoRegistro.sector.areaSubdisciplinaNombre = nuevaSolicitud.areaConocimiento?.subdisciplina?.nombre || null;
  }

  private existeRegistroFisica() {
    this.reniecytApi()
      .getExisteRegistrofisica(this.account.cvu)
      .then(res => {
        if (res?.data) this.tipoSolicitanteOptions = this.tipoSolicitanteOptions.filter((option: any) => option.value.id !== '2');
      })
      .catch(err => {
        if (err?.response?.data?.detail) {
          this.alertService().showError(this, err.response.data.detail);
        } else {
          this.alertService().showError(this, this.$t('error.internalServerError').toString());
        }
        this.tipoSolicitanteOptions = this.tipoSolicitanteOptions.filter((option: any) => option.value.id !== '2');
      });
  }

  public existeRFC() {
    if (!this.$v.nuevaSolicitud.rfc.$error) {
      this.reniecytApi()
        .getExisteRfc(this.nuevaSolicitud.rfc)
        .then(res => {
          if (res.data) {
            this.alertService().showWarning(this, this.$t('nueva-solicitud.alert.rfcExiste').toString());
            this.nuevaSolicitud.rfc = '';
          }
        })
        .catch(err => {
          if (err?.response?.data?.detail) {
            this.alertService().showError(this, err.response.data.detail);
          } else {
            this.alertService().showError(this, this.$t('error.internalServerError').toString());
          }
        });
    }
  }

  private setAreaConocimientoInRegistro(areaConocimiento: AreaConocimiento): void {
    // Asignar valores a sector AreaConocimiento
    this.nuevoRegistro.sector.areaAreaId = areaConocimiento?.area?.id || null;
    this.nuevoRegistro.sector.areaAreaNombre = areaConocimiento?.area?.nombre || null;
    this.nuevoRegistro.sector.areaCampoId = areaConocimiento?.campo?.id || null;
    this.nuevoRegistro.sector.areaCampoNombre = areaConocimiento?.campo?.nombre || null;
    this.nuevoRegistro.sector.areaDisciplinaId = areaConocimiento?.disciplina?.id || null;
    this.nuevoRegistro.sector.areaDisciplinaNombre = areaConocimiento?.disciplina?.nombre || null;
    this.nuevoRegistro.sector.areaSubdisciplinaId = areaConocimiento?.subdisciplina?.id || null;
    this.nuevoRegistro.sector.areaSubdisciplinaNombre = areaConocimiento?.subdisciplina?.nombre || null;
  }

  public updateRegistroSector(actualizaregistroConocimiento) {
    if (this.validaAreaConocimiento(this.nuevaSolicitud)) {
      //ocupa validar solo Area
      this.setAreaConocimientoInRegistro(actualizaregistroConocimiento);

      this.reniecytApi()
        .actualizaSector(this.idRegistro, this.nuevoRegistro.sector)
        .then(res => {
          this.alertService().showSuccess(this, this.$t('nueva-solicitud.alert.save').toString());
          this.$router.push({ name: 'ConsultaSolicitudes' });
        })
        .catch(err => {
          if (err?.response?.data?.detail) {
            this.alertService().showError(this, err.response.data.detail);
          } else {
            this.alertService().showError(this, this.$t('error.internalServerError').toString());
          }
        });
    } else {
      this.$v.nuevaSolicitud.$touch();
      this.validChildArea = true;
    }
  }

  private consultaDatosGeneralesRegistro() {
    this.reniecytApi()
      .getDatosGenerales(this.idRegistro)
      .then(res => {
        this.nuevaSolicitud.razonSocial = res.data.registro.razonSocial;
        this.nuevaSolicitud.rfc = res.data.registro.rfc;
        this.noRegistro = res.data.registro.noDeRegistro;
      })
      .catch(err => {
        if (err?.response?.data?.detail) {
        } else {
          this.alertService().showError(this, this.$t('error.internalServerError').toString());
        }
      });
  }

  public updateRegistro(nSolictud) {
    if (!this.$v.nuevaSolicitud.$invalid) {
      if (this.sectorPrivada && this.nuevaSolicitud.tipoInstitucionEmpresa.id == Constants.CLAVE_TIPO_INST_EMPRESA) {
        if (
          !this.validaAreaConocimiento(this.nuevaSolicitud) ||
          !this.validaOcde(this.nuevaSolicitud) ||
          !this.validaScian(this.nuevaSolicitud)
        ) {
          this.validChildArea = true;
          this.validChildOcde = true;
          this.validChildScian = true;
          return;
        }
      }
      this.setSolicitudInActualizaRegistro(nSolictud);
      this.reniecytApi()
        .updateRegistro(this.idRegistro, this.actualizaRegistro)
        .then(res => {
          this.alertService().showSuccess(this, this.$t('nueva-solicitud.alert.save').toString());
          this.$v.nuevaSolicitud.$reset();
          this.$router.push({ name: 'ConsultaSolicitudes' });
        })
        .catch(err => {
          if (err?.response?.data?.detail) {
            this.alertService().showError(this, err.response.data.detail);
          } else {
            this.alertService().showError(this, this.$t('error.internalServerError').toString());
          }
        });
    } else {
      if (this.nuevaSolicitud.rfc === null && this.nuevaSolicitud.sector.id != Constants.CLAVE_SECTOR_SOCIAL) this.nuevaSolicitud.rfc = '';
      this.$v.nuevaSolicitud.$touch();
    }
  }

  private setSolicitudInActualizaRegistro(editSolicitud: any): void {
    // Asignar valores a registro
    this.actualizaRegistro.registro.id = this.idRegistro;
    this.actualizaRegistro.registro.noDeRegistro = this.noRegistro;
    this.actualizaRegistro.registro.tipoSolicitudId = editSolicitud.tipoSolicitante?.id || null;
    this.actualizaRegistro.registro.tipoSolicitudNombre = editSolicitud.tipoSolicitante?.nombre || null;
    this.actualizaRegistro.registro.rfc = editSolicitud.rfc || null;
    this.actualizaRegistro.registro.nombre = editSolicitud.nombre || null;
    this.actualizaRegistro.registro.primerApellido = editSolicitud.primerApellido || null;
    this.actualizaRegistro.registro.segundoApellido = editSolicitud.segundoApellido || null;
    this.actualizaRegistro.registro.actividadId = editSolicitud.actividadPreponderante?.id || null;
    this.actualizaRegistro.registro.actividadNombre = editSolicitud.actividadPreponderante?.nombre || null;
    this.actualizaRegistro.registro.sectorId = editSolicitud.sector?.id || null;
    this.actualizaRegistro.registro.sectorNombre = editSolicitud.sector?.nombre || null;
    this.actualizaRegistro.registro.razonSocial = editSolicitud.razonSocial || null;
    this.actualizaRegistro.registro.tipoInstitucionId =
      editSolicitud.tipoInstitucionEmpresa?.id || editSolicitud.tipoInstitucionEntidad?.id || null;
    this.actualizaRegistro.registro.tipoInstitucionNombre =
      editSolicitud.tipoInstitucionEmpresa?.nombre || editSolicitud.tipoInstitucionEntidad?.nombre || null;
    this.actualizaRegistro.registro.nivelId = editSolicitud.nivel?.id || null;
    this.actualizaRegistro.registro.nivelNombre = editSolicitud.nivel?.nombre || null;
    this.actualizaRegistro.registro.figuraJuridicaId = editSolicitud.figuraJuridica?.id || null;
    this.actualizaRegistro.registro.figuraJuridicaNombre = editSolicitud.figuraJuridica?.nombre || null;
    this.actualizaRegistro.registro.grupoSocialId = editSolicitud.grupoSocial?.id || null;
    this.actualizaRegistro.registro.grupoSocialNombre = editSolicitud.grupoSocial?.nombre || null;
    this.actualizaRegistro.registro.grupoSocialEspecificar = editSolicitud.especificar || null;
    this.actualizaRegistro.registro.clasificacionId = editSolicitud.clasificacion?.id || null;
    this.actualizaRegistro.registro.clasificacionNombre = editSolicitud.clasificacion?.nombre || null;
    this.actualizaRegistro.registro.tamanioId = editSolicitud.tamanioEmpresa?.id || null;
    this.actualizaRegistro.registro.tamanioNombre = editSolicitud.tamanioEmpresa?.nombre || null;

    // Asignar valores a sector
    this.actualizaRegistro.sector.scianSectorId = editSolicitud.scian?.sector?.id || null;
    this.actualizaRegistro.sector.scianSectorNombre = editSolicitud.scian?.sector?.nombre || null;
    this.actualizaRegistro.sector.scianSubsectorId = editSolicitud.scian?.subsector?.id || null;
    this.actualizaRegistro.sector.scianSubsectorNombre = editSolicitud.scian?.subsector?.nombre || null;
    this.actualizaRegistro.sector.scianRamaId = editSolicitud.scian?.rama?.id || null;
    this.actualizaRegistro.sector.scianRamaNombre = editSolicitud.scian?.rama?.nombre || null;
    this.actualizaRegistro.sector.scianSubramaId = editSolicitud.scian?.subrama?.id || null;
    this.actualizaRegistro.sector.scianSubramaNombre = editSolicitud.scian?.subrama?.nombre || null;
    this.actualizaRegistro.sector.scianClaseId = editSolicitud.scian?.clase?.id || null;
    this.actualizaRegistro.sector.scianClaseNombre = editSolicitud.scian?.clase?.nombre || null;
    this.actualizaRegistro.sector.ocdeSectorId = editSolicitud.ocde?.sector?.id || null;
    this.actualizaRegistro.sector.ocdeSectorNombre = editSolicitud.ocde?.sector?.nombre || null;
    this.actualizaRegistro.sector.ocdeDivisionId = editSolicitud.ocde?.division?.id || null;
    this.actualizaRegistro.sector.ocdeDivisionNombre = editSolicitud.ocde?.division?.nombre || null;
    this.actualizaRegistro.sector.ocdeGrupoId = editSolicitud.ocde?.grupo?.id || null;
    this.actualizaRegistro.sector.ocdeGrupoNombre = editSolicitud.ocde?.grupo?.nombre || null;
    this.actualizaRegistro.sector.ocdeClaseId = editSolicitud.ocde?.clase?.id || null;
    this.actualizaRegistro.sector.ocdeClaseNombre = editSolicitud.ocde?.clase?.nombre || null;
    this.actualizaRegistro.sector.areaAreaId = editSolicitud.areaConocimiento?.area?.id || null;
    this.actualizaRegistro.sector.areaAreaNombre = editSolicitud.areaConocimiento?.area?.nombre || null;
    this.actualizaRegistro.sector.areaCampoId = editSolicitud.areaConocimiento?.campo?.id || null;
    this.actualizaRegistro.sector.areaCampoNombre = editSolicitud.areaConocimiento?.campo?.nombre || null;
    this.actualizaRegistro.sector.areaDisciplinaId = editSolicitud.areaConocimiento?.disciplina?.id || null;
    this.actualizaRegistro.sector.areaDisciplinaNombre = editSolicitud.areaConocimiento?.disciplina?.nombre || null;
    this.actualizaRegistro.sector.areaSubdisciplinaId = editSolicitud.areaConocimiento?.subdisciplina?.id || null;
    this.actualizaRegistro.sector.areaSubdisciplinaNombre = editSolicitud.areaConocimiento?.subdisciplina?.nombre || null;
  }
}
